import type { MonitoringClient } from "@version2/monitoring/client";
import { initMonitoringClient, loadHyperDXScript } from "@version2/monitoring/client";

type WebVitalMetric = {
  id: string;
  name: string;
  value: number;
  rating?: string;
};

let monitoringClient: MonitoringClient | null = null;
let initializationPromise: Promise<MonitoringClient | null> | null = null;

const resolveEnvironment = (): string => {
  const env =
    import.meta.env.VITE_MONITORING_ENVIRONMENT ||
    import.meta.env.MODE ||
    "development";
  const normalized = env.toLowerCase();
  const allowed = ["production", "staging", "development", "test"];
  if (allowed.includes(normalized)) {
    return normalized;
  }
  return "development";
};

const parseSampleRate = (): number | undefined => {
  const sample = import.meta.env.VITE_MONITORING_TRACE_SAMPLE_RATE;
  if (!sample) return undefined;
  const parsed = Number(sample);
  if (Number.isNaN(parsed)) return undefined;
  return Math.min(Math.max(parsed, 0), 1);
};

const shouldEnableMonitoring = (): boolean => {
  if (import.meta.env.VITE_MONITORING_ENABLED === "false") {
    return false;
  }
  if (import.meta.env.VITE_MONITORING_ENABLED === "true") {
    return true;
  }
  return Boolean(import.meta.env.VITE_HYPERDX_API_KEY);
};

export const initClientMonitoring = async (): Promise<MonitoringClient | null> => {
  if (monitoringClient) {
    return monitoringClient;
  }
  if (initializationPromise) {
    return initializationPromise;
  }
  if (!shouldEnableMonitoring()) {
    return null;
  }

  const apiKey = import.meta.env.VITE_HYPERDX_API_KEY;
  if (!apiKey) {
    console.warn("[Monitoring] Missing VITE_HYPERDX_API_KEY. Skipping client monitoring init.");
    return null;
  }

  const serviceName =
    import.meta.env.VITE_MONITORING_SERVICE_NAME || "landing-website-client";

  initializationPromise = (async () => {
    try {
      await loadHyperDXScript();
      monitoringClient = initMonitoringClient({
        serviceName,
        apiKey,
        environment: resolveEnvironment(),
        serviceVersion: import.meta.env.VITE_MONITORING_SERVICE_VERSION,
        enabled: true,
        traceSampleRate: parseSampleRate(),
        privacy: {
          maskAllInputs: true,
          maskSelectors: [
            ".sensitive",
            "[data-private]",
            "[type='password']",
            "[data-mask='true']",
          ],
        },
      });

      if (import.meta.env.VITE_MONITORING_ENABLE_SESSION_REPLAY !== "false") {
        monitoringClient.startReplay();
      }

      return monitoringClient;
    } catch (error) {
      console.error("[Monitoring] Failed to initialize client monitoring", error);
      return null;
    } finally {
      initializationPromise = null;
    }
  })();

  return initializationPromise;
};

export const getMonitoringClient = (): MonitoringClient | null => monitoringClient;

export const trackWebVitalMetric = (metric: WebVitalMetric): void => {
  const client = getMonitoringClient();
  if (!client) return;

  client.captureEvent("web_vital", {
    id: metric.id,
    name: metric.name,
    value: metric.value,
    rating: metric.rating,
  });
};
